import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';

export type GlobalAudioTrack = {
  id: string;
  src: string;
  title: string;
  coverUrl?: string;
  productId?: string;
};

type PlayOptions = {
  startTime?: number;
  autoplay?: boolean;
};

type GlobalAudioContextValue = {
  enabled: boolean;
  setEnabled: (enabled: boolean) => void;
  track: GlobalAudioTrack | null;
  isPlaying: boolean;
  currentTime: number;
  duration: number;
  volume: number;
  muted: boolean;
  play: (track: GlobalAudioTrack, options?: PlayOptions) => Promise<void>;
  pause: () => void;
  resume: () => Promise<void>;
  togglePlayPause: () => Promise<void>;
  stop: () => void;
  seek: (timeSeconds: number) => void;
  setVolume: (volume: number) => void;
  setMuted: (muted: boolean) => void;
};

const STORAGE_KEY = 'haapa.globalAudio.enabled';

const GlobalAudioContext = createContext<GlobalAudioContextValue | null>(null);

const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value));

export const GlobalAudioProvider = ({ children }: { children: ReactNode }) => {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [enabled, setEnabledState] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.localStorage.getItem(STORAGE_KEY) === 'true';
  });
  const [track, setTrack] = useState<GlobalAudioTrack | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolumeState] = useState(1);
  const [muted, setMutedState] = useState(false);

  const setEnabled = useCallback((nextEnabled: boolean) => {
    setEnabledState(nextEnabled);
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(STORAGE_KEY, String(nextEnabled));
    }
  }, []);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const handlePlay = () => setIsPlaying(true);
    const handlePause = () => setIsPlaying(false);
    const handleTime = () => setCurrentTime(audio.currentTime || 0);
    const handleLoaded = () => setDuration(Number.isFinite(audio.duration) ? audio.duration : 0);
    const handleVolume = () => {
      setVolumeState(Number.isFinite(audio.volume) ? audio.volume : 1);
      setMutedState(Boolean(audio.muted));
    };

    audio.addEventListener('play', handlePlay);
    audio.addEventListener('pause', handlePause);
    audio.addEventListener('timeupdate', handleTime);
    audio.addEventListener('loadedmetadata', handleLoaded);
    audio.addEventListener('durationchange', handleLoaded);
    audio.addEventListener('volumechange', handleVolume);

    return () => {
      audio.removeEventListener('play', handlePlay);
      audio.removeEventListener('pause', handlePause);
      audio.removeEventListener('timeupdate', handleTime);
      audio.removeEventListener('loadedmetadata', handleLoaded);
      audio.removeEventListener('durationchange', handleLoaded);
      audio.removeEventListener('volumechange', handleVolume);
    };
  }, []);

  const seek = useCallback((timeSeconds: number) => {
    const audio = audioRef.current;
    if (!audio) return;
    const safeTime = clamp(timeSeconds, 0, Number.isFinite(audio.duration) ? audio.duration : timeSeconds);
    try {
      audio.currentTime = safeTime;
      setCurrentTime(safeTime);
    } catch {
      // ignore
    }
  }, []);

  const setVolume = useCallback((nextVolume: number) => {
    const audio = audioRef.current;
    const safeVolume = clamp(nextVolume, 0, 1);
    if (!audio) {
      setVolumeState(safeVolume);
      return;
    }
    audio.volume = safeVolume;
    setVolumeState(safeVolume);
  }, []);

  const setMuted = useCallback((nextMuted: boolean) => {
    const audio = audioRef.current;
    if (!audio) {
      setMutedState(nextMuted);
      return;
    }
    audio.muted = nextMuted;
    setMutedState(nextMuted);
  }, []);

  const play = useCallback(
    async (nextTrack: GlobalAudioTrack, options: PlayOptions = {}) => {
      const audio = audioRef.current;
      if (!audio) return;

      const { startTime, autoplay = true } = options;
      setTrack(nextTrack);

      if (!enabled) {
        setEnabled(true);
      }

      if (audio.src !== nextTrack.src) {
        audio.src = nextTrack.src;
      }

      const applyStartTime = () => {
        if (typeof startTime === 'number' && Number.isFinite(startTime) && startTime >= 0) {
          try {
            const maxTime = Number.isFinite(audio.duration) && audio.duration > 0 ? audio.duration : startTime;
            audio.currentTime = clamp(startTime, 0, maxTime);
          } catch {
            // ignore
          }
        }
      };

      applyStartTime();
      audio.load();

      if (typeof startTime === 'number') {
        const handleMeta = () => {
          applyStartTime();
          audio.removeEventListener('loadedmetadata', handleMeta);
        };
        audio.addEventListener('loadedmetadata', handleMeta);
      }

      if (autoplay) {
        try {
          await audio.play();
        } catch {
          // ignore autoplay restrictions
        }
      }
    },
    [enabled, setEnabled],
  );

  const pause = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) return;
    audio.pause();
  }, []);

  const resume = useCallback(async () => {
    const audio = audioRef.current;
    if (!audio) return;
    try {
      await audio.play();
    } catch {
      // ignore
    }
  }, []);

  const togglePlayPause = useCallback(async () => {
    if (isPlaying) {
      pause();
      return;
    }
    await resume();
  }, [isPlaying, pause, resume]);

  const stop = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) return;
    audio.pause();
    try {
      audio.currentTime = 0;
    } catch {
      // ignore
    }
    setTrack(null);
    setCurrentTime(0);
    setDuration(0);
  }, []);

  const value = useMemo<GlobalAudioContextValue>(
    () => ({
      enabled,
      setEnabled,
      track,
      isPlaying,
      currentTime,
      duration,
      volume,
      muted,
      play,
      pause,
      resume,
      togglePlayPause,
      stop,
      seek,
      setVolume,
      setMuted,
    }),
    [
      currentTime,
      duration,
      enabled,
      isPlaying,
      muted,
      pause,
      play,
      resume,
      seek,
      setEnabled,
      setMuted,
      setVolume,
      stop,
      togglePlayPause,
      track,
      volume,
    ],
  );

  return (
    <GlobalAudioContext.Provider value={value}>
      <audio ref={audioRef} preload="metadata" className="hidden" />
      {children}
    </GlobalAudioContext.Provider>
  );
};

export const useGlobalAudio = (): GlobalAudioContextValue => {
  const ctx = useContext(GlobalAudioContext);
  if (!ctx) {
    throw new Error('useGlobalAudio must be used within GlobalAudioProvider');
  }
  return ctx;
};

